<script>
  import { beforeNavigate, goto } from '$app/navigation';
  import { page, updated } from '$app/stores';
  import Icon from '$lib/components/Icon.svelte';
  import ToastContainer from '$lib/features/app/components/ToastContainer.svelte';
  import { modal, overlayModal } from '$lib/features/app/modals';
  import { init, ROLES, token } from '$lib/features/auth/authStore';
  import SessionExpiryWatcher from '$lib/features/auth/components/SessionExpiryWatcher.svelte';
  import '$lib/features/auth/services';
  // Import to initialize token subscriber
  import ActivityLog from '$lib/features/logs/components/ActivityLog.svelte';
  import SideBar from '$lib/features/navigation/components/sidebar/SideBar.svelte';
  import VideoHistorySidebar from '$lib/features/video-meeting/components/VideoHistorySidebar.svelte';
  import VideoSidebar from '$lib/features/video-meeting/components/VideoSidebar.svelte';
  import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query';
  import { setDefaultOptions } from 'date-fns';
  import { sv } from 'date-fns/locale';
  import { onMount } from 'svelte';
  import { twMerge } from 'tailwind-merge';
  import '../app.css';

  /**
   * @typedef {Object} Props
   * @property {import('svelte').Snippet} [children]
   */

  /** @type {Props} */
  let { children } = $props();
  setDefaultOptions({ locale: sv, weekStartsOn: 1 });

  const queryClient = new QueryClient();
  const tokenFromQuery = $page.url.searchParams.get('token');

  let mounted = $state(false);

  // redirect to login if token is null and not on login page
  $effect(() => {
    if (
      mounted &&
      $page.url.pathname !== '/login' &&
      $page.url.pathname !== '/reset-password' &&
      $page.url.pathname !== '/logout' &&
      $token === null
    ) {
      goto('/logout');
    }
  });

  beforeNavigate(async function ({ to, cancel }) {
    if (to?.route?.id === '/') {
      if ($ROLES.medic || $ROLES.supervisor) {
        cancel();
        goto('/dashboard', { replaceState: true });
      } else {
        cancel();
        goto('/companies', { replaceState: true });
      }
    }
  });

  onMount(async () => {
    init(tokenFromQuery);
    const newUrl = new URL($page.url);
    newUrl?.searchParams?.delete('token');
    if (!$page.error) {
      goto(newUrl.toString(), { replaceState: true });
    }
    if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
      localStorage.setItem('umami.disabled', 'true');
    }
    mounted = true;
  });

  let renderedModal = $state({ props: {} });
  let renderedOverlayModal = $state({ props: {} });
  $effect.pre(() => {
    if ($modal) renderedModal = $modal;
  });
  $effect.pre(() => {
    if ($overlayModal) renderedOverlayModal = $overlayModal;
  });
</script>

<svelte:head>
  {#if import.meta.env.VITE_ENVIRONMENT === 'development'}
    <title>OneLab Dev</title>
    <link rel="icon" href={`/icons/favicon-dev.png`} />
  {:else}
    <title>OneLab</title>
  {/if}
  <meta name="description" content="OneLab" />
</svelte:head>

<QueryClientProvider client={queryClient}>
  <ActivityLog params={$page.params} path={$page.url.pathname} />
  <div class="bg-white text-gray-7 dark:bg-black dark:text-gray-3">
    {#if $updated.current}
      <div class="fixed inset-0 bottom-auto z-30 bg-yellow">
        <div
          class="relative flex items-center justify-center gap-4 px-8 py-4 text-black"
        >
          <Icon size="xl" icon="update" />
          <div>
            <div class="text-[1rem] font-medium">
              Ny version av Backoffice tillgänglig!
            </div>
            <div>
              Spara dina ändringar och avsluta videosamtal innan du laddar om
              sidan för att få den senaste uppdateringen.
            </div>
          </div>
          <button
            class="rounded-md border border-black p-2 px-4 transition hover:bg-black/10"
            onclick={() => location.reload()}
          >
            Ladda om nu
          </button>
        </div>
      </div>
    {/if}

    {#if page.error || $token === null}
      {@render children?.()}
    {:else if $token !== false}
      <div
        data-testid="app"
        id="lol"
        class={twMerge('app flex h-full', $updated.current && 'pt-16')}
      >
        <SideBar />
        <SessionExpiryWatcher />
        <main class="flex-1 overflow-hidden">
          {@render children?.()}
        </main>
        <aside>
          <VideoSidebar />
          <VideoHistorySidebar />
        </aside>
      </div>
      {#if $modal}
        {@const SvelteComponent = renderedModal.modalComponent}
        <SvelteComponent {...renderedModal.props} />
      {/if}
      {#if $overlayModal}
        <!-- Rendered AFTER $modal so it paints on top while keeping the
             parent modal mounted (preserves form state on cancel). -->
        {@const SvelteComponent_1 = renderedOverlayModal.modalComponent}
        <SvelteComponent_1 {...renderedOverlayModal.props} />
      {/if}
    {/if}

    <ToastContainer />
  </div>
</QueryClientProvider>
